// src/components/TeamsSection.tsx
import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { secureStorage } from "@/utils/secureStorage";

import { TeamsList } from "./ui/TeamsList";
import { TeamPosts } from "./ui/TeamPosts";
import { CreateTeamDialog } from "./ui/CreateTeamDialog";

import { Team } from "../types/Team";
import { TeamRepository } from "@/infrastructure/repositories/TeamRepository";

export function TeamsSection() {
  const teamRepo = new TeamRepository();

  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);

  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [showDialog, setShowDialog] = useState(false);

  const userType =
  secureStorage.get<string>("user_type") ?? "swimmer";
  const coachId = Number(localStorage.getItem("coach_id"));
  const swimmerId = Number(localStorage.getItem("current_user_swimmer_id"));
  const [teamToDelete, setTeamToDelete] = useState<Team | null>(null);
  const [deleting, setDeleting] = useState(false);

  // NEW: selected team → opens posts screen
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);

  // --------------------------------------
  // LOAD TEAMS (Coach or Swimmer)
  // --------------------------------------
  const loadTeams = async () => {
    try {
      setLoading(true);
      setTeams([]);

      if (userType === "coach") {
        if (!coachId) return;
        const res = await teamRepo.getCoachTeams(coachId, 0);
        setTeams(res.data || []);
      } else {
        if (!swimmerId) return;
        const res = await teamRepo.getSwimmerTeams(swimmerId);
        setTeams(res.data || []);
      }
    } catch (err) {
      setDialogMessage("Failed to load teams");
      setShowDialog(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTeams();
  }, []);

  // --------------------------------------
  // CREATE TEAM
  // --------------------------------------
  const handleCreateTeam = async (form: any) => {
    try {
      if (!coachId) {
        setDialogMessage("Coach ID missing");
        setShowDialog(true);
        return;
      }

      const res = await teamRepo.createTeam(
        coachId,
        form.name,
        form.city ?? "",
        form.state ?? "",
        form.country ?? "",
        form.info ?? "",
        (form.profile_image as string | undefined) ?? "",
        (form.cover_image as string | undefined) ?? ""
      );

      if (res.success) {
        setDialogMessage("Team created successfully!");
        setShowDialog(true);
        setShowCreateTeam(false);
        loadTeams();
      } else {
        setDialogMessage(res.message || "Failed to create team");
        setShowDialog(true);
      }
    } catch {
      setDialogMessage("Something went wrong");
      setShowDialog(true);
    }
  };

  // --------------------------------------
  // OPEN TEAM POSTS
  // --------------------------------------
  if (selectedTeam) {
    return (
      <TeamPosts
        team={selectedTeam}
        onBack={() => setSelectedTeam(null)}
      />
    );
  }


const handleDeleteTeam = async () => {
  if (!teamToDelete || !coachId) return;

  try {
    setDeleting(true);

    const res = await teamRepo.deleteTeam(
      Number(teamToDelete.team_id),
      coachId
    );

    if (res.success) {
      setTeams((prev) =>
        prev.filter((t) => t.team_id !== teamToDelete.team_id)
      );
      setDialogMessage("Team deleted successfully");
        loadTeams();
    } else {
      setDialogMessage(res.message || "Failed to delete team");
    }
  } catch {
    setDialogMessage("Something went wrong");
  } finally {
    setDeleting(false);
    setTeamToDelete(null);
    setShowDialog(true);
  }
};


  // --------------------------------------
  // RENDER TEAM LIST
  // --------------------------------------
  return (
    <div className="mt-4">

      {loading && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
          </div>
      )}

      {!loading && (
        <>
          {/* SHOW CREATE BUTTON ONLY FOR COACH */}
         {userType === "coach" && (
          <div className="flex justify-end mb-4">
            <Button onClick={() => setShowCreateTeam(true)}>
              + Create Team
            </Button>
          </div>
        )}

          <TeamsList
            list={teams}
            userType={userType}
            onSelect={(team) => setSelectedTeam(team)}
            onDelete={(team) => setTeamToDelete(team)}
          />  
        </>
      )}

      {/* CREATE TEAM POPUP */}
      <CreateTeamDialog
        key={showCreateTeam ? "open" : "closed"}
        open={showCreateTeam}
        onOpenChange={setShowCreateTeam}
        onSubmit={handleCreateTeam}
      />

      {/* ALERT */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-sm">
          <DialogTitle>Notification</DialogTitle>
          <p className="text-muted-foreground mt-2">{dialogMessage}</p>

          <div className="mt-4 flex justify-end">
            <Button onClick={() => setShowDialog(false)}>OK</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!teamToDelete} onOpenChange={() => setTeamToDelete(null)}>
        <DialogContent className="max-w-sm">
          <DialogTitle>Delete Team?</DialogTitle>

          <p className="text-muted-foreground mt-2">
            Are you sure you want to delete this team? This action cannot be undone.
          </p>

          <div className="mt-4 flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setTeamToDelete(null)}
              disabled={deleting}
            >
              Cancel
            </Button>

            <Button
              variant="destructive"
              onClick={handleDeleteTeam}
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
